import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, forkJoin, map, of, tap } from 'rxjs';

import { ApiResponse, FrontendBlogCategory, FrontendBlogSilo } from './api.models';
import { SAVING_ARENA_API_CONFIG } from './api.config';

/**
 * Which single-segment `/blogs/<slug>` URLs are categories and which are hubs (silos).
 *
 * Both live at the same URL shape now — `/blogs/coupon-strategy` is a category, `/blogs/smart-
 * shopping-hub` is a hub — so the router needs to know which is which before it can pick a
 * component. `blogCategoryMatcher` / `blogHubMatcher` in app.routes.ts are plain synchronous
 * functions the router calls during URL matching: they cannot inject anything or await a request,
 * so they read this module state, which an app initializer fills before the first navigation.
 *
 * This mirrors `discount-type-registry`, which solves the same problem for `/student-discount`.
 */

let categorySlugs = new Set<string>();
let hubSlugs = new Set<string>();

// SSR bootstraps the app once per request, so without a TTL this would fire two lookups on the
// critical path of every render. Module state outlives the request on the server, so a short TTL
// collapses that to roughly one refresh per minute per process while still picking up a newly
// published category or hub without a restart.
const RELOAD_AFTER_MS = 60_000;
let loadedAtMs = 0;

const clean = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

/** True when the slug belongs to a published blog category. */
export function isBlogCategorySlug(slug: string): boolean {
  return categorySlugs.has(clean(slug));
}

/** True when the slug belongs to a published hub (silo). */
export function isBlogHubSlug(slug: string): boolean {
  return hubSlugs.has(clean(slug));
}

/** Replaces the registry. Exported for tests and the initializer below. */
export function setBlogSlugs(categories: readonly string[], hubs: readonly string[]): void {
  categorySlugs = new Set(categories.map(clean).filter(Boolean));
  hubSlugs = new Set(hubs.map(clean).filter(Boolean));
}

export function getBlogSlugs(): { categories: readonly string[]; hubs: readonly string[] } {
  return { categories: [...categorySlugs], hubs: [...hubSlugs] };
}

/**
 * App initializer: loads both slug lists before the router's first navigation.
 *
 * Fails soft. An empty registry means `/blogs/<slug>` 404s until the next load, which is bad but
 * survivable; refusing to boot the site because one lookup failed would be far worse. On error the
 * TTL is cleared so the next render retries instead of sitting on an empty list for a full minute.
 */
export function loadBlogSlugs() {
  const http = inject(HttpClient);
  const config = inject(SAVING_ARENA_API_CONFIG);

  const now = Date.now();
  if (loadedAtMs && now - loadedAtMs < RELOAD_AFTER_MS) return of(undefined);
  loadedAtMs = now;

  // apiBaseUrl already ends in /api/frontend — do not repeat it here.
  //
  // `fields=slug` asks for a slug-only projection. This runs before the first navigation of every
  // cold page load and reads nothing but `.slug` (see setBlogSlugs below), yet the full responses
  // were 97KB and 8KB — the silo one is three quarters embedded store records — and cost the API
  // two populates, two assignment lookups and a grouped count each time. The light responses are
  // 0.7KB and 0.5KB and list exactly the same slugs in the same order.
  return forkJoin({
    categories: http
      .get<ApiResponse<Pick<FrontendBlogCategory, 'slug'>[]>>(`${config.apiBaseUrl}/blog-categories?fields=slug`)
      .pipe(catchError(() => of(null))),
    hubs: http
      .get<{ data?: Pick<FrontendBlogSilo, 'slug'>[] }>(`${config.apiBaseUrl}/blog-silos?limit=200&fields=slug`)
      .pipe(catchError(() => of(null))),
  }).pipe(
    tap(({ categories, hubs }) => {
      const categoryList = Array.isArray(categories?.data) ? categories.data : [];
      const hubList = Array.isArray(hubs?.data) ? hubs.data : [];

      // Keep whatever is already known if BOTH lookups came back empty — that is far more likely
      // to be a failed request than a site with no categories at all.
      if (!categoryList.length && !hubList.length && (categorySlugs.size || hubSlugs.size)) {
        loadedAtMs = 0;
        return;
      }

      setBlogSlugs(
        categoryList.map((category) => category.slug ?? ''),
        hubList.map((hub) => hub.slug ?? ''),
      );
    }),
    catchError(() => {
      loadedAtMs = 0;
      return of(null);
    }),
    map(() => undefined),
  );
}

/** Test seam: forget the TTL so the next call refetches. */
export function resetBlogSlugCache(): void {
  loadedAtMs = 0;
  categorySlugs = new Set();
  hubSlugs = new Set();
}
